import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useTrackedAircraft } from "features/map/layers/aircraft-tracking/useTrackedAircraft"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import * as Icons from "@nova/component-icons"
import { ButtonTertiary } from "@nova/component-btn"
import { ReactNode } from "react"
import { DottedAircraftIcon } from "../DottedAircraftIcon"
import classNames from "classnames"
import { useTimeAgo } from "@nova/toolkit-pure-hooks"
import { clearActivePopup, setActiveAircraftId } from "../slice"

export const AircraftPopup = ({ id }: { id: string }) => {
    const dispatch = useAppDispatch()
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const properties = useTrackedAircraft({ orgId, id })
    const timeAgo = useTimeAgo({ timestamp: properties?.timestamp })
    if (!properties) return null
    return (
        <div className="pointer-events-auto w-[300px] rounded-lg bg-black-800 px-5 py-3 flex flex-col gap-3 backdrop-blur-sm">
            <div className="flex flex-col gap-2">
                <div className="flex justify-between items-center">
                    <DottedAircraftIcon
                        type={properties.type}
                        source={properties.source}
                        className="h-9 w-auto"
                    />
                    <div className="flex flex-col items-end gap-1">
                        <div className="font-mono text-white-400 text-xs">{timeAgo}</div>
                        <div className="font-mono text-white-400 text-xs">
                            {properties.source === "adsb" ? "ADS-B" : "Fleet Manager"}
                        </div>
                    </div>
                </div>
                <div className="flex flex-col">
                    <div className="text-2xl text-white">{properties.callsign}</div>
                </div>
            </div>
            <div className="w-full h-px bg-white-200" />
            <div className="flex flex-col gap-3">
                <AircraftPopupDetail
                    label="Position"
                    value={`${properties.lat.toFixed(4)}°, ${properties.lng.toFixed(4)}°`}
                />
                <div className="flex items-center gap-3">
                    <AircraftPopupDetail
                        label="Altitude"
                        value={`${properties.altitude.toFixed(2)} ft`}
                    />
                    <AircraftPopupDetail
                        label="Vertical rate"
                        value={
                            properties.verticalRate
                                ? `${Math.abs(properties.verticalRate).toFixed(2)} ft/min`
                                : "-"
                        }
                        labelIcon={
                            properties.verticalRate ? (
                                <Icons.ExtraSmallIcon
                                    Icon={Icons.KeyboardDoubleArrowUp}
                                    className={classNames("text-green-500", {
                                        "text-green-500": properties.verticalRate > 0,
                                        "text-red-500 rotate-180": properties.verticalRate < 0,
                                    })}
                                />
                            ) : undefined
                        }
                    />
                </div>
                <div className="flex items-center gap-3">
                    <AircraftPopupDetail
                        label="Speed"
                        value={`${properties.speed.toFixed(2)} kt`}
                    />
                    <AircraftPopupDetail
                        label="Ground track"
                        value={`${properties.heading.toFixed(1)}°`}
                    />
                </div>
            </div>
            {properties.source === "adsb" && (
                <ButtonTertiary
                    title="More info"
                    className="w-full"
                    onClick={() => {
                        dispatch(setActiveAircraftId(id))
                        dispatch(clearActivePopup())
                    }}
                />
            )}
        </div>
    )
}

const AircraftPopupDetail = ({
    label,
    labelIcon,
    value,
}: {
    label: string
    labelIcon?: ReactNode
    value: string
}) => {
    return (
        <div className="flex-1 flex flex-col gap-0.5">
            <div className="flex items-center gap-0.5">
                <div className="text-xs text-gray-300">{label}</div>
                {labelIcon}
            </div>
            <div className="font-mono text-white">{value}</div>
        </div>
    )
}
